import { fold, type ProgrammeState, type RepwiseEvent } from '@repwise/domain';
import type { SyncStatus } from '@repwise/sync';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { Repo } from './repo.ts';
import { diag, timed } from '../diag/log.ts';

export const RepoContext = createContext<Repo | null>(null);
export function useRepo(): Repo {
  const r = useContext(RepoContext);
  if (!r) throw new Error('RepoContext missing');
  return r;
}

/** The active profile's event log, live. */
export function useEvents(): RepwiseEvent[] | null {
  const { store } = useRepo();
  const [events, setEvents] = useState<RepwiseEvent[] | null>(null);
  useEffect(() => {
    let alive = true;
    // A wiped store (J8/J13) is closed just before the reload; late reads are moot. Only the LATEST load may land:
    // reads can finish out of order (a cached read beats an IndexedDB one), and an older one must never win.
    let seq = 0;
    const load = () => {
      const mine = ++seq;
      const t0 = performance.now();
      void store.events().then((e) => {
        const ms = performance.now() - t0;
        if (ms >= 50) diag('perf', 'events.load', { ms, count: e.length }, 'warn');
        if (alive && mine === seq) setEvents(e);
      }, () => undefined);
    };
    load();
    const off = store.onChange(load);
    return () => {
      alive = false;
      off();
    };
  }, [store]);
  return events;
}

export function useProgramme(events: RepwiseEvent[] | null): ProgrammeState | null {
  return useMemo(() => (events ? timed('domain', 'fold', () => fold(events), { count: events.length }) : null), [events]);
}

export function useSyncStatus(): SyncStatus | null {
  const { store, engine } = useRepo();
  const [s, setS] = useState<SyncStatus | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () => void engine.status().then((x) => alive && setS(x), () => undefined);
    load();
    const off = store.onChange(load);
    const offStatus = engine.onStatus(load);
    const t = setInterval(load, 15000);
    return () => {
      alive = false;
      off();
      offStatus();
      clearInterval(t);
    };
  }, [store, engine]);
  return s;
}

/** A clock that ticks while mounted (rest ring, undo chip). */
export function useNow(intervalMs = 250): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** Keep the screen awake during a workout (Android browsers; iOS 18.4+ in the installed app); fails quietly elsewhere. */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    const acquire = () =>
      navigator.wakeLock
        .request('screen')
        .then((l) => (lock = l))
        .catch(() => undefined);
    void acquire();
    const onVis = () => document.visibilityState === 'visible' && void acquire();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      void lock?.release().catch(() => undefined);
    };
  }, [active]);
}

export { isInstalled as isStandalone } from './platform.ts';
